/**
 * 링크 주소를 화면에 걸기 전에 통과시키는 체.
 *
 * React 는 본문 텍스트는 자동으로 이스케이프하지만 **`href`·`src` 는 걸러 주지 않는다** —
 * `javascript:alert(1)` 을 그대로 렌더하고 경고만 찍는다. 그래서 서버·외부에서 온 주소를
 * 그대로 거는 자리(첨부 링크, 수집한 뉴스 원문, 사용자가 입력한 외부 URL)는 여기를 거친다.
 * `<iframe src>` 은 더 나쁘다: 그 프레임이 같은 오리진이라 주입된 스크립트가 앱과 같은 권한을 갖는다.
 *
 * 통과시키는 것만 적는다(allowlist). 막을 것을 나열하는 방식은 새 스킴이 생길 때마다 뚫린다.
 */

/** 앱이 스스로 만든 blob: 은 이 문서에 등록된 객체만 가리킨다 — 남의 blob URL 은 애초에 열리지 않는다. */
const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:', 'blob:']);

/**
 * 메일 링크 — 이름이 있으면 `"홍길동" <주소>` 로 실어 메일 프로그램의 받는 사람 칸에 이름째 들어가게 한다
 * (사용자 지시 2026-10-26). 주소만 넘기면 받는 사람이 주소로만 서고 주소록에도 이름 없이 남는다.
 */
export function mailtoHref(email: string, name?: string | null): string {
  const address = email.trim();
  const label = (name ?? '').replace(/["<>\\]/g, '').trim();
  return label ? `mailto:${encodeURIComponent(`"${label}" <${address}>`)}` : `mailto:${address}`;
}

/**
 * 걸어도 되는 주소면 그대로, 아니면 `undefined`.
 *
 * `undefined` 를 돌려주는 이유: `<a href={safeHref(x)}>` 가 href 없는 앵커가 되어 **아무 일도
 * 일어나지 않는다**. 빈 문자열이나 `#` 로 바꾸면 눌렸을 때 화면이 맨 위로 튀거나 라우팅이 흔들린다.
 *
 * `data:` 는 넣지 않는다 — `data:text/html,...` 이 곧 XSS 다. 미리보기는 전부 blob: 을 쓴다.
 */
export function safeHref(value: string | null | undefined): string | undefined {
  if (!value) return undefined;

  const trimmed = value.trim();
  // 공백뿐인 값. 아래 URL 파싱에 넘기면 현재 페이지 주소로 해석돼 통과해 버린다.
  if (!trimmed) return undefined;

  // 앱 안의 절대경로(`/api/...`). `//evil.com` 은 스킴만 생략한 **외부** 주소이므로 여기서 빠져
  // 아래 URL 파싱으로 간다(그쪽에서 http/https 인지 다시 본다).
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;

  try {
    // 스킴 판정은 손으로 하지 않는다 — `java\nscript:` 처럼 파서만 아는 변형이 있다.
    const parsed = new URL(trimmed, window.location.origin);
    return ALLOWED_PROTOCOLS.has(parsed.protocol) ? trimmed : undefined;
  } catch {
    return undefined;
  }
}
